<template>
  <div class="timeTrackingTable" v-if="tableStateStore">
    <DataTable
      :key="tableStateConfig.name"
      class="p-datatable-sm"
      :value="timeTrackings"
      :loading="isLoading"
      :rowClass="rowClass"
      :rowHover="true"
      dataKey="number"
      :lazy="true"
      :filters.sync="tableStateStore.filters"
      stateStorage="local"
      :stateKey="tableStateStore.filterName()"
      filterDisplay="row"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      :totalRecords="getTimeTrackingCount"
      :paginator="true"
      :rows.sync="tableStateStore.pagination.pageSize"
      :first="tableStateStore.skip()"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="'Anzeigen ' + '{first} bis {last} von {totalRecords} Einträgen'"
      :rowsPerPageOptions="[10, 20, 50, 100, 200]"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left"></div>
          <div class="table-header-right">
            <button class="btn btn-narrow btn-inverse ml-2 mb-2" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <Button
              v-if="canEditTimeTrackings"
              class="btn btn-narrow btn-inverse mr-2"
              @click="showNewTimeTrackingDialog"
            >
              <i class="fa fa-plus mr-2"></i>
              {{ $t('new') }}
            </Button>
            <ButtonWait
              v-if="canExportTimeTrackings"
              :startCallback="startTimeTrackingExportBuild"
              :pollCallback="fetchExportBuildState"
              :disabled="isExportBuilding"
              @timeout="refreshUser"
              class="pull-right"
            >
              <template v-slot:buttonText>
                {{ $t('generate_time_tracking_list') }}
              </template>
            </ButtonWait>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="startTimeDay"
        :header="$t('day')"
        sortable
        :sortField="'startTime'"
        :headerStyle="{ width: '180px' }"
        :showFilterMenu="false"
        filterField="dateRange"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.startTime">
            {{ formatDateWithDots(slotProps.data.startTime) }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            @input="filterCallback()"
          />
        </template>
      </Column>

      <Column
        field="startTime"
        :header="$t('start')"
        :styles="{ width: '100px' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <strong> {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong>
        </template>
      </Column>

      <Column
        field="stopTime"
        :header="$t('end')"
        :styles="{ width: '100px' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <strong v-if="slotProps.data[slotProps.column.field]">
            {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong
          >
          <strong v-else>
            <LoopingRhombusesSpinner
              :animation-duration="12000"
              :rhombus-size="9"
              color="#495057"
            />
          </strong>
        </template>
      </Column>

      <Column field="duration" :header="$t('duration')" sortable :headerStyle="{ width: '100px' }">
        <template #body="slotProps">
          <span>
            {{ formatDuration(slotProps.data.duration || 0) }}
          </span>
        </template>
      </Column>

      <Column field="timeTrackingType" :header="$t('timeTrackingType')" :showFilterMenu="false">
        <template #body="slotProps">
          <span>
            {{ $t(slotProps.data.timeTrackingType || 'WORKING_TIME') }}
          </span>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.timeTrackingTypes"
            :options="timeTrackingTypes"
            @input="onTimeTrackingTypeFilter"
            optionLabel="label"
            :placeholder="$t('timeTrackingType')"
          >
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="timeTrackingState"
        :header="$t('state')"
        :styles="{ width: '7%' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge :variant="getTimeTrackingStateColor(slotProps.data.timeTrackingState)">
            {{ $t(slotProps.data.timeTrackingState) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.timeTrackingStates"
            :options="timeTrackingStates"
            @input="onTimeTrackingStateFilter"
            optionLabel="label"
            :placeholder="$t('state')"
          >
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="user"
        :header="$t('employee')"
        sortable
        :showFilterMenu="false"
        filterField="timeTrackingUserSearch"
      >
        <template #body="slotProps">
          <div>
            {{ slotProps.data.user.firstname }}
            {{ slotProps.data.user.lastname }}
          </div>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('firstname') + ', ' + $t('lastname')"
          />
        </template>
      </Column>

      <Column
        field="project"
        v-if="!isProjectContext"
        :header="$t('project')"
        sortable
        :showFilterMenu="false"
        filterField="timeTrackingCustomerSearch"
      >
        <template #body="slotProps">
          <span
            v-if="
              slotProps.data &&
                (slotProps.data.project ||
                  (slotProps.data.appointment && slotProps.data.appointment.project))
            "
          >
            <!-- Auditor: only show project number without link/customer -->
            <template v-if="hasAuditorRedaction">
              <span
                v-if="
                  slotProps.data.appointment &&
                    slotProps.data.appointment.project &&
                    slotProps.data.appointment.project.number
                "
              >
                {{ slotProps.data.appointment.project.number.toString().padStart(4, '0') }}
              </span>
              <span v-else-if="slotProps.data.project && slotProps.data.project.number">
                {{ slotProps.data.project.number.toString().padStart(4, '0') }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <!-- Non-auditor: original rich display -->
            <template v-else>
              <template v-if="slotProps.data.appointment && slotProps.data.appointment.project">
                <router-link
                  v-if="
                    slotProps.data.appointment.project && slotProps.data.appointment.project.number
                  "
                  target="_blank"
                  :to="{
                    name: 'ProjectEditPage',
                    params: { projectNumber: slotProps.data.appointment.project.number },
                  }"
                  >{{ slotProps.data.appointment.project.number.toString().padStart(4, '0') }}
                  <i class="fa fa-sm fa-external-link"></i>
                </router-link>
                &nbsp;-&nbsp;
                {{ slotProps.data.appointment.project.customer.firstname }}
                {{ slotProps.data.appointment.project.customer.lastname }}
                ,
                {{ slotProps.data.appointment.project.customer.zip }}
                {{ slotProps.data.appointment.project.customer.city }}
              </template>
              <template v-else-if="slotProps.data.project">
                <router-link
                  v-if="slotProps.data.project && slotProps.data.project.number"
                  target="_blank"
                  :to="{
                    name: 'ProjectEditPage',
                    params: { projectNumber: slotProps.data.project.number },
                  }"
                  >{{ slotProps.data.project.number.toString().padStart(4, '0') }}
                  <i class="fa fa-sm fa-external-link"></i>
                </router-link>
                &nbsp;-&nbsp;
                {{ slotProps.data.project.customer.firstname }}
                {{ slotProps.data.project.customer.lastname }}
                ,
                {{ slotProps.data.project.customer.zip }}
                {{ slotProps.data.project.customer.city }}
              </template>
            </template>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="
              $t('project') +
                ' ' +
                $t('number') +
                ', ' +
                $t('firstname') +
                ', ' +
                $t('lastname') +
                ', ' +
                $t('zip') +
                ', ' +
                $t('city')
            "
          />
        </template>
      </Column>

      <Column
        field="appointment"
        :header="$t('appointment')"
        sortable
        :showFilterMenu="false"
        filterField="timeTrackingAppointmentSearch"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.appointment">
            <div class="appointment-info">
              <span class="appointment-number">
                #{{ slotProps.data.appointment.number.toString().padStart(4, '0') }}
              </span>
              <span
                class="appointment-datetime"
                v-if="slotProps.data.appointment.installationStartAt"
              >
                {{ formatAppointmentDateTime(slotProps.data.appointment.installationStartAt) }}
                <span v-if="slotProps.data.appointment.installationEndAt">
                  - {{ formatAppointmentTime(slotProps.data.appointment.installationEndAt) }}
                </span>
              </span>
              <span class="appointment-type">
                <b-badge
                  :variant="getAppointmentTypeColor(slotProps.data.appointment.appointmentType)"
                >
                  {{ $t(slotProps.data.appointment.appointmentType) }}
                </b-badge>
              </span>
            </div>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('appointment') + ' ' + $t('number')"
          />
        </template>
      </Column>

      <Column v-if="canEditTimeTrackings" :styles="{ 'min-width': '80px' }">
        <template #body="slotProps">
          <button class="btn btn-icon" @click="showEditDialog(slotProps.data)">
            <i class="link-icon fa fa-lg fa-edit mr-2"></i>
          </button>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :header="$t('time_tracking') + ' ' + (isNewTimeTracking ? $t('new') : $t('edit'))"
      :visible.sync="displayEditDialog"
      :contentStyle="{ overflow: 'visible' }"
      :modal="true"
    >
      <div v-if="editedTimeTracking">
        <div class="user-info">
          <div class="user-name" v-if="!isNewTimeTracking">
            {{ editedTimeTracking.user.firstname }} {{ editedTimeTracking.user.lastname }}
          </div>
          <div class="info-row">
            <div class="date-info">
              {{ $dayjs(editedTimeTracking.startTime).format('DD.MM.YYYY') }}<br />
              <template v-if="editedTimeTracking.stopTime">
                <span
                  v-if="
                    $dayjs(editedTimeTracking.startTime).format('DD.MM.YYYY') ===
                      $dayjs(editedTimeTracking.stopTime).format('DD.MM.YYYY')
                  "
                >
                  {{ $dayjs(editedTimeTracking.startTime).format('HH:mm') }} -
                  {{ $dayjs(editedTimeTracking.stopTime).format('HH:mm') }}
                </span>
                <span v-else>
                  {{ $dayjs(editedTimeTracking.startTime).format('DD.MM.YYYY HH:mm') }} -
                  {{ $dayjs(editedTimeTracking.stopTime).format('DD.MM.YYYY HH:mm') }}
                </span>
                <br />
                {{
                  formatDuration(
                    getDurationInMinutes(editedTimeTracking.startTime, editedTimeTracking.stopTime)
                  )
                }}
                Stunden
              </template>
              <template v-else-if="editedTimeTracking.timeTrackingState === 'STARTED'">
                {{ $dayjs(editedTimeTracking.startTime).format('HH:mm') }} -
                <span>{{ $t('running') }}...</span>
                <br />
                {{ formatDuration(getDurationInMinutes(editedTimeTracking.startTime, new Date())) }}
                Stunden
              </template>
            </div>
            <div class="state-badge">
              <b-badge :variant="getTimeTrackingStateColor(editedTimeTracking.timeTrackingState)">
                {{ $t(editedTimeTracking.timeTrackingState) }}
              </b-badge>
            </div>
          </div>
        </div>

        <div class="type-input mb-3" v-if="editedTimeTracking.timeTrackingState !== 'STARTED'">
          <label>{{ $t('timeTrackingType') }}</label>
          <select v-model="editedTimeTracking.timeTrackingType" class="form-control">
            <option
              v-for="timeTrackingType in timeTrackingTypes"
              :key="timeTrackingType.value"
              :value="timeTrackingType.value"
            >
              {{ timeTrackingType.label }}
            </option>
          </select>
        </div>

        <div class="time-inputs" v-if="editedTimeTracking.timeTrackingState !== 'STARTED'">
          <div class="user-input" v-if="isNewTimeTracking">
            <label>{{ $t('employee') }}</label>
            <multiselect
              class="multiselect-users"
              v-model="editedTimeTracking.user"
              :options="filteredUsers"
              track-by="number"
              v-on:search-change="onFilterUsers"
              :filter="true"
              :internal-search="false"
              :multiple="false"
              :show-labels="false"
              :allow-empty="false"
              :deselectLabel="$t('deselectLabel')"
              selectLabel=""
              selectedLabel=""
              placeholder="Mitarbeiter"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ option.firstname }} {{ option.lastname }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ option.firstname }} {{ option.lastname }}
              </template>
              <template slot="tag" slot-scope="{ option }">
                <div>{{ option.firstname }} {{ option.lastname }}</div>
              </template>
              <template slot="noResult">
                <div>{{ $t('no_results') }}</div>
              </template>
            </multiselect>
          </div>
          <div class="appointment-input">
            <label>{{ $t('appointment') }}</label>
            <MultiselectAppointments
              v-model="editedTimeTracking.appointment"
              :project-context="isProjectContext ? project : null"
              :prevent-deselect="true"
              @select="onAppointmentSelect"
            />
          </div>
          <div v-if="!isProjectContext" class="project-input">
            <label>{{ $t('project') }}</label>
            <MultiselectProjects
              v-model="editedTimeTracking.project"
              :disabled="Boolean(editedTimeTracking.appointment)"
              @select="onProjectSelect"
            />
          </div>
          <div class="time-row">
            <div class="time-input">
              <label>{{ $t('start') }}</label>
              <div class="time-input-with-calendar">
                <Calendar
                  class="mb-2"
                  v-model="editedTimeTracking.startDateOnly"
                  :showTime="false"
                  :showOtherMonths="true"
                  :modal="true"
                  :appendTo="'body'"
                  @date-select="onStartDateSelect"
                />
              </div>
              <Calendar
                v-model="editedTimeTracking.startTimeOnly"
                :timeOnly="true"
                :showTime="true"
                :stepMinute="15"
                :manualInput="true"
                hourFormat="24"
                @time-select="onStartTimeSelect"
              />
            </div>
            <div class="time-input">
              <label>{{ $t('end') }}</label>
              <div class="time-input-with-calendar">
                <Calendar
                  class="mb-2"
                  v-model="editedTimeTracking.stopDateOnly"
                  :showTime="false"
                  :showOtherMonths="true"
                  :modal="true"
                  :appendTo="'body'"
                  @date-select="onStopDateSelect"
                />
              </div>
              <Calendar
                v-model="editedTimeTracking.stopTimeOnly"
                :timeOnly="true"
                :showTime="true"
                :stepMinute="15"
                :manualInput="true"
                hourFormat="24"
                @time-select="onStopTimeSelect"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <ConfirmDeleteButton
          v-if="!isNewTimeTracking && canEditTimeTrackings"
          :confirmCallback="onConfirmDelete"
          class="mr-auto"
        />
        <Button class="btn btn-narrow btn-default ml-2 mb-2" @click="onCancelEdit">
          {{ $t('cancel') }}
        </Button>
        <template v-if="editedTimeTracking && editedTimeTracking.timeTrackingState === 'STARTED'">
          <Button class="btn btn-narrow btn-danger ml-2 mb-2" @click="stopTimeTracking">
            {{ $t('stop') }}
          </Button>
        </template>
        <Button
          v-else
          class="btn btn-narrow btn-inverse ml-2 mb-2"
          @click="onSaveEdit"
          :disabled="!isFormValid"
        >
          {{ $t('save') }}
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Calendar from 'primevue/calendar';
import { getTimeTrackingStateColor, getAppointmentTypeColor } from '@/helpers/colors';
import ButtonWait from '@/components/ButtonWait.vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import { LoopingRhombusesSpinner } from 'epic-spinners';
import ConfirmDeleteButton from '@/components/ConfirmDeleteButton';
import MultiselectAppointments from '@/components/MultiselectAppointments.vue';
import MultiselectProjects from '@/components/MultiselectProjects.vue';
import Multiselect from 'vue-multiselect';
import { coversAccessLevel } from '@/helpers/accessLevel';
import { TIME_TRACKING_ACCESS_NODE } from '@/navigation/accessNodes';

const USER_SELECTION_PAGINATION = { skip: 0, pageSize: 50 };
const EXPORT_BUILD_IN_PROGRESS = 'IN_PROGRESS';

export default {
  name: 'TimeTrackingTable',
  components: {
    DataTable,
    Column,
    Calendar,
    MultiSelect,
    ButtonWait,
    InputText,
    LoopingRhombusesSpinner,
    ConfirmDeleteButton,
    Dialog,
    Button,
    MultiselectAppointments,
    MultiselectProjects,
    Multiselect,
  },
  props: {
    /** Set inside the block project-time-tracking: the table uses the block's paths. */
    project: { type: Object },
    /**
     * Matrix-Authz: no level to write, on the block project-time-tracking or on the view
     * time-tracking (set by the block or the view); Legacy-Authz the old role field decides.
     */
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      filteredUsers: [],
      displayEditDialog: false,
      editedTimeTracking: null,
      isNewTimeTracking: false,

      defaultFilters: {
        projectNumber: { value: null },
        timeTrackingCustomerSearch: {
          value: null,
        },
        timeTrackingUserSearch: {
          value: null,
        },
        timeTrackingAppointmentSearch: {
          value: null,
        },
        dateRange: {
          range: [
            this.$dayjs()
              .startOf('day')
              .toDate(),
            this.$dayjs()
              .endOf('day')
              .toDate(),
          ],
        },
      },
      defaultCustomFilters: {
        timeTrackingStates: [],
        timeTrackingTypes: [],
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getCurrentUser', 'isOrganizationRoleNavigationEnabled']),
    ...mapGetters([
      'getTimeTrackings',
      'getEnumValues',
      'getTimeTrackingCount',
      'getTimeTrackingTotalHours',
      'isLoading',
      'getTableState',
      'getAccessLevel',
    ]),
    isAuditor() {
      return (
        this.getCurrentUser &&
        this.getCurrentUser.authorizationAttributes &&
        this.getCurrentUser.authorizationAttributes.role === 'AUDITOR'
      );
    },
    /**
     * New, edit and delete. Switch on: the level decides (see feature page projekt-montage);
     * switch off: the old role field, as before.
     */
    canEditTimeTrackings() {
      return this.isOrganizationRoleNavigationEnabled ? !this.isReadOnly : !this.isAuditor;
    },
    /**
     * Matrix-Authz: every level of the block or the view exports. The table is rendered in a
     * project only inside the block, so there the user has a block level.
     */
    canExportTimeTrackings() {
      if (!this.isOrganizationRoleNavigationEnabled) {
        return !this.isAuditor;
      }
      if (this.isProjectContext) {
        return true;
      }
      return coversAccessLevel(this.getAccessLevel(TIME_TRACKING_ACCESS_NODE), 'viewer');
    },
    /** Block and view share the export file of the user; one export at a time. */
    isExportBuilding() {
      return this.getCurrentUser?.timeTrackingExportBuildState === EXPORT_BUILD_IN_PROGRESS;
    },
    /** The project of the block's paths; the view uses the global paths. */
    blockProjectNumber() {
      return this.project?.number;
    },
    /** Customer hidden from auditors, with the switch off only. */
    hasAuditorRedaction() {
      return !this.isOrganizationRoleNavigationEnabled && this.isAuditor;
    },
    isFormValid() {
      if (!this.editedTimeTracking) return false;
      return (
        this.editedTimeTracking.user &&
        this.editedTimeTracking.startTime &&
        this.editedTimeTracking.stopTime
      );
    },
    getTimeTrackingTotalHours() {
      return this.timeTrackings.reduce((total, tracking) => {
        return total + tracking.duration;
      }, 0);
    },
    timeTrackings() {
      return this.getTimeTrackings;
    },
    isProjectContext() {
      return this.project;
    },
    timeTrackingStates() {
      if (!this.getEnumValues('TimeTrackingState')) {
        return [];
      }
      return this.getEnumValues('TimeTrackingState').map((timeTrackingState) => {
        return { value: timeTrackingState, label: this.$t(timeTrackingState) };
      });
    },
    timeTrackingTypes() {
      if (!this.getEnumValues('TimeTrackingTypes')) {
        return [];
      }
      return this.getEnumValues('TimeTrackingTypes').map((timeTrackingType) => {
        return { value: timeTrackingType, label: this.$t(timeTrackingType) };
      });
    },
    tableStateName() {
      if (this.project && this.project.number) {
        return `timeTrackings-project-${this.project.number}`;
      }

      return 'timeTrackings';
    },
    /** In the block the server fixes the project, so the client sends no project filter. */
    tableStateConfig() {
      const { projectNumber, ...filtersWithoutProject } = this.defaultFilters;
      return {
        name: this.tableStateName,
        filters: this.isProjectContext ? filtersWithoutProject : this.defaultFilters,
        customFilters: { ...this.defaultCustomFilters },
        pagination: {
          page: 0,
          pageSize: 50,
        },
        sorting: {
          sortField: 'startTime',
          sortOrder: -1,
        },
      };
    },
    tableStateStore() {
      return this.getTableState(this.tableStateConfig.name);
    },
  },
  watch: {
    async tableStateName(newTableStateName, oldTableStateName) {
      if (newTableStateName === oldTableStateName) {
        return;
      }

      await this.initTableState(this.tableStateConfig);
      await this.loadTimeTrackingData();
    },
  },
  methods: {
    ...mapActions([
      'initTableState',
      'fetchEnumValues',
      'fetchUserAttachmentUrl',
      'fetchTimeTracking',
      'updateTimeTracking',
      'createTimeTracking',
      'deleteTimeTracking',
      'fetchUserSelection',
    ]),
    ...mapActions('auth', ['buildUserTimeTrackingExport', 'refreshUser']),

    async startTimeTrackingExportBuild() {
      if (this.getCurrentUser) {
        const userId = this.getCurrentUser.id;
        const totalRecords = this.getTimeTrackingCount;
        const page = 0;
        const pageSize = totalRecords > 0 ? totalRecords : this.tableStateStore.pagination.pageSize;
        const sortField = this.tableStateStore.sorting.sortField;
        const sortOrder = this.tableStateStore.sorting.sortOrder;
        const filters = this.buildTimeTrackingFilters();

        await this.buildUserTimeTrackingExport({
          userId,
          page,
          pageSize,
          sortField,
          sortOrder,
          filters,
          projectNumber: this.blockProjectNumber,
        });
      }
    },

    async fetchExportBuildState() {
      await this.refreshUser();

      if (this.getCurrentUser.timeTrackingExportBuildState === 'FINISHED') {
        this.downloadTimeTrackingExport('timeTrackingExportAttachment');
      }
      return this.getCurrentUser.timeTrackingExportBuildState;
    },

    async downloadTimeTrackingExport(attachmentType) {
      try {
        const url = await this.fetchUserAttachmentUrl({
          userId: this.getCurrentUser.id,
          attachmentType: attachmentType,
        });
        window.open(url);
      } catch (error) {
        console.error('Error fetching attachment URL:', error);
      }
    },
    async loadTimeTrackingData() {
      try {
        await this.fetchTimeTracking({
          page: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
          sortField: this.tableStateStore.sorting.sortField,
          sortOrder: this.tableStateStore.sorting.sortOrder,
          filters: this.buildTimeTrackingFilters(),
          projectNumber: this.blockProjectNumber,
        });
      } catch (error) {
        console.error('Fehler beim Laden der Zeitverfolgungsdaten:', error);
      }
    },
    formatDateWithDots(datetime) {
      const dateObj = new Date(datetime);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}.${month}.${year}`;
    },

    buildTimeTrackingFilters() {
      const filters = {
        ...this.tableStateStore.filters,
        ...this.tableStateStore.customFilters,
        timeTrackingStates: this.tableStateStore.customFilters.timeTrackingStates,
        timeTrackingTypes: this.tableStateStore.customFilters.timeTrackingTypes,
      };

      // the block's paths fix the project on the server; a stored filter must not narrow it
      if (this.isProjectContext) {
        delete filters.projectNumber;
      }

      if (filters.dateRange?.range && filters.dateRange.range[1]) {
        filters.dateRange = {
          ...filters.dateRange,
          range: [
            filters.dateRange.range[0],
            this.$dayjs(filters.dateRange.range[1])
              .endOf('day')
              .toDate(),
          ],
        };
      }

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null) {
          delete filters[key];
        }
      });

      return filters;
    },

    async onFilter(event) {
      await this.loadTimeTrackingData();
    },
    /**
     * Funktion zum Filtern des Zeitverfolgungsstatus.
     * @param {Array} value - Die ausgewählten Statuswerte, nach denen gefiltert werden soll.
     */
    async onTimeTrackingStateFilter(value) {
      const filteredStates = {
        timeTrackingStates: this.timeTrackingStates.filter((timeTrackingState) =>
          value.includes(timeTrackingState)
        ),
      };

      this.tableStateStore.setCustomFilters(filteredStates);

      await this.loadTimeTrackingData();
    },
    async onTimeTrackingTypeFilter(value) {
      const filteredTypes = {
        timeTrackingTypes: this.timeTrackingTypes.filter((timeTrackingType) =>
          value.includes(timeTrackingType)
        ),
      };

      this.tableStateStore.setCustomFilters(filteredTypes);

      await this.loadTimeTrackingData();
    },
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    rowClass(data) {
      return data.number === '_new' ? 'new' : null;
    },

    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
    },

    formatAppointmentDateTime(datetime) {
      if (!datetime) return '';
      return this.$dayjs(datetime).format('DD.MM.YYYY HH:mm');
    },

    formatAppointmentTime(datetime) {
      if (!datetime) return '';
      return this.$dayjs(datetime).format('HH:mm');
    },

    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      await this.loadTimeTrackingData();
    },

    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      await this.loadTimeTrackingData();
    },

    async onConfirmDelete() {
      if (this.editedTimeTracking && this.editedTimeTracking.number) {
        try {
          await this.deleteTimeTracking({
            timeTrackingNumber: this.editedTimeTracking.number,
            projectNumber: this.blockProjectNumber,
          });
          this.displayEditDialog = false;
          this.editedTimeTracking = null;
          this.isNewTimeTracking = false;
          await this.loadTimeTrackingData();
        } catch (error) {
          console.error('Error deleting time tracking:', error);
        }
      }
    },

    getTimeTrackingStateColor,
    getAppointmentTypeColor,

    showNewTimeTrackingDialog() {
      const now = new Date();
      const startTime = this.initStartTime(now);
      const stopTime = this.initStopTime(startTime);

      console.log('showNewTimeTrackingDialog - initial startTime:', startTime);
      console.log('showNewTimeTrackingDialog - initial stopTime:', stopTime);

      this.editedTimeTracking = {
        number: '_new',
        // Backend properties
        startTime: startTime,
        stopTime: stopTime,
        // Separate UI input properties
        startDateOnly: startTime,
        startTimeOnly: startTime,
        stopDateOnly: stopTime,
        stopTimeOnly: stopTime,
        timeTrackingState: 'STOPPED',
        timeTrackingType: 'WORKING_TIME',
        user: null,
        appointment: null,
        project: this.isProjectContext ? this.project : null,
      };
      this.isNewTimeTracking = true;
      this.displayEditDialog = true;
      this.loadUserSelection(null);

      // Ensure backend properties are properly synced
      this.$nextTick(() => {
        console.log('showNewTimeTrackingDialog - calling updateBackendProperties');
        this.updateBackendProperties();
      });
    },

    initStartTime(date) {
      return this.$dayjs(date)
        .hour(8)
        .minute(0)
        .second(0)
        .toDate();
    },

    initStopTime(date) {
      return this.$dayjs(this.initStartTime(date))
        .add(8, 'hours')
        .toDate();
    },

    resolveAssignedProject(timeTracking) {
      if (timeTracking?.appointment?.project && timeTracking?.project) {
        return timeTracking.appointment.project;
      }

      if (timeTracking?.project) {
        return timeTracking.project;
      }

      if (this.isProjectContext) {
        return this.project;
      }

      return null;
    },

    onProjectSelect(project) {
      if (!this.editedTimeTracking) {
        return;
      }

      if (this.editedTimeTracking.appointment?.project) {
        this.editedTimeTracking.project = this.editedTimeTracking.appointment.project;
        return;
      }

      this.editedTimeTracking.project = project;
    },

    onAppointmentSelect(appointment) {
      if (!this.editedTimeTracking) {
        return;
      }

      this.editedTimeTracking.appointment = appointment;

      if (appointment?.project && this.editedTimeTracking.project) {
        this.editedTimeTracking.project = appointment.project;
      }
    },

    showEditDialog(timeTracking) {
      // Create a copy of the timeTracking object to avoid direct mutation
      const startTime = new Date(timeTracking.startTime);
      const stopTime =
        timeTracking.timeTrackingState === 'STARTED'
          ? null
          : timeTracking.stopTime
          ? new Date(timeTracking.stopTime)
          : this.$dayjs(timeTracking.startTime)
              .add(8, 'hours')
              .toDate();

      this.editedTimeTracking = {
        ...timeTracking,
        // Backend properties
        startTime: startTime,
        stopTime: stopTime,
        // Separate UI input properties
        startDateOnly: startTime,
        startTimeOnly: startTime,
        stopDateOnly: stopTime,
        stopTimeOnly: stopTime,
        timeTrackingType: timeTracking.timeTrackingType || 'WORKING_TIME',
      };
      this.isNewTimeTracking = false;
      this.displayEditDialog = true;

      // Ensure backend properties are properly synced
      this.$nextTick(() => {
        this.updateBackendProperties();
      });
    },

    onCancelEdit() {
      this.displayEditDialog = false;
      this.editedTimeTracking = null;
      this.isNewTimeTracking = false;
    },

    async onSaveEdit() {
      // console.log('onSaveEdit():', this.editedTimeTracking);
      if (this.editedTimeTracking) {
        if (this.isNewTimeTracking && !this.editedTimeTracking.user) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('error'),
            detail: this.$t('please_select_employee'),
            life: 3000,
          });
          return;
        }
        await this.save(this.editedTimeTracking);
        await this.loadTimeTrackingData();
      }
      this.displayEditDialog = false;
      this.editedTimeTracking = null;
      this.isNewTimeTracking = false;
    },

    async save(timeTracking) {
      timeTracking.project = this.resolveAssignedProject(timeTracking);

      // Ensure backend properties are up-to-date before saving
      this.updateBackendProperties();

      console.log('save():', timeTracking);

      if (timeTracking.number === '_new') {
        await this.createTimeTracking({ timeTracking, projectNumber: this.blockProjectNumber });
      } else {
        await this.updateTimeTracking({ timeTracking, projectNumber: this.blockProjectNumber });
      }
    },

    async stopTimeTracking() {
      if (this.editedTimeTracking) {
        this.editedTimeTracking.stopTime = new Date();
        this.editedTimeTracking.timeTrackingState = 'STOPPED';
        await this.save(this.editedTimeTracking);
        this.displayEditDialog = false;
        this.editedTimeTracking = null;
      }
    },

    getDurationInMinutes(startTime, stopTime) {
      return this.$dayjs(stopTime).diff(this.$dayjs(startTime), 'minute');
    },

    onStartDateSelect(date) {
      console.log('onStartDateSelect():', date);
      // Update only the date part, preserve existing time
      const currentTime = this.$dayjs(this.editedTimeTracking.startTimeOnly);
      this.editedTimeTracking.startDateOnly = date;
      this.editedTimeTracking.startTimeOnly = this.$dayjs(date)
        .hour(currentTime.hour())
        .minute(currentTime.minute())
        .second(currentTime.second())
        .toDate();

      // Validate: if start date > stop date, adjust stop date
      const startDate = this.$dayjs(date);
      const stopDate = this.$dayjs(this.editedTimeTracking.stopDateOnly);
      if (startDate.format('YYYY-MM-DD') > stopDate.format('YYYY-MM-DD')) {
        const currentStopTime = this.$dayjs(this.editedTimeTracking.stopTimeOnly);
        this.editedTimeTracking.stopDateOnly = date;
        this.editedTimeTracking.stopTimeOnly = this.$dayjs(date)
          .hour(currentStopTime.hour())
          .minute(currentStopTime.minute())
          .second(currentStopTime.second())
          .toDate();
      }

      // Update backend properties
      this.updateBackendProperties();
    },

    onStopDateSelect(date) {
      console.log('onStopDateSelect():', date);
      // Update only the date part, preserve existing time
      const currentTime = this.$dayjs(this.editedTimeTracking.stopTimeOnly);
      this.editedTimeTracking.stopDateOnly = date;
      this.editedTimeTracking.stopTimeOnly = this.$dayjs(date)
        .hour(currentTime.hour())
        .minute(currentTime.minute())
        .second(currentTime.second())
        .toDate();

      // Update backend properties
      this.updateBackendProperties();
    },

    onStartTimeSelect(time) {
      console.log('onStartTimeSelect():', time);
      // Update only the time part, preserve existing date
      const currentDate = this.$dayjs(this.editedTimeTracking.startDateOnly);
      const newTime = this.$dayjs(time);
      this.editedTimeTracking.startTimeOnly = currentDate
        .hour(newTime.hour())
        .minute(newTime.minute())
        .second(newTime.second())
        .toDate();

      // Update backend properties
      this.updateBackendProperties();
    },

    onStopTimeSelect(time) {
      console.log('onStopTimeSelect():', time);
      // Update only the time part, preserve existing date
      const currentDate = this.$dayjs(this.editedTimeTracking.stopDateOnly);
      const newTime = this.$dayjs(time);
      this.editedTimeTracking.stopTimeOnly = currentDate
        .hour(newTime.hour())
        .minute(newTime.minute())
        .second(newTime.second())
        .toDate();

      // Update backend properties
      this.updateBackendProperties();
    },

    // Update backend properties by combining separate date/time inputs
    updateBackendProperties() {
      if (this.editedTimeTracking) {
        // Update startTime
        if (this.editedTimeTracking.startDateOnly && this.editedTimeTracking.startTimeOnly) {
          const newStartTime = this.$dayjs(this.editedTimeTracking.startDateOnly)
            .hour(this.$dayjs(this.editedTimeTracking.startTimeOnly).hour())
            .minute(this.$dayjs(this.editedTimeTracking.startTimeOnly).minute())
            .second(this.$dayjs(this.editedTimeTracking.startTimeOnly).second())
            .toDate();
          console.log('updateBackendProperties - setting startTime:', newStartTime);
          this.editedTimeTracking.startTime = newStartTime;
        }

        // Update stopTime
        if (this.editedTimeTracking.stopDateOnly && this.editedTimeTracking.stopTimeOnly) {
          const newStopTime = this.$dayjs(this.editedTimeTracking.stopDateOnly)
            .hour(this.$dayjs(this.editedTimeTracking.stopTimeOnly).hour())
            .minute(this.$dayjs(this.editedTimeTracking.stopTimeOnly).minute())
            .second(this.$dayjs(this.editedTimeTracking.stopTimeOnly).second())
            .toDate();
          console.log('updateBackendProperties - setting stopTime:', newStopTime);
          this.editedTimeTracking.stopTime = newStopTime;
        }
      }
    },

    /**
     * Remove all filters
     * @param {*} event
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTimeTrackingData();
    },

    async onFilterUsers(query) {
      await this.loadUserSelection(query || null);
    },

    /** Only the new form selects a user, so only an editor loads the selection. */
    async loadUserSelection(search) {
      this.filteredUsers = await this.fetchUserSelection({
        search,
        pagination: USER_SELECTION_PAGINATION,
      });
    },
  },

  async created() {
    await this.initTableState(this.tableStateConfig);
  },

  async mounted() {
    await this.fetchEnumValues('TimeTrackingState');
    await this.fetchEnumValues('TimeTrackingTypes');

    await this.loadTimeTrackingData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-menu-button {
  display: none;
}
::v-deep .p-column-filter-clear-button {
  display: none;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.state-badge {
  margin-left: 1rem;
}

.user-info {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.date-info {
  color: #666;
  font-size: 0.95rem;
}

.time-inputs {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.user-input {
  width: 100%;
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .multiselect-users {
    width: 100%;
  }
}

.project-input {
  width: 100%;
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .multiselect-project {
    width: 100%;
  }
}

.time-row {
  display: flex;
  gap: 1rem;
  width: 100%;
}

.time-input {
  flex: 1;
}

.time-input label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.time-input-with-calendar {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .p-button {
    padding: 0.5rem;
    min-width: 2.5rem;
    height: 2.5rem;
  }
}

.multiselect-users {
  width: 100%;
}

::v-deep .p-datepicker-title {
  display: flex;
}

::v-deep .p-calendar .p-inputtext {
  width: 100%;
}

::v-deep .p-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.date-calendar {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.appointment-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.appointment-number {
  font-weight: 500;
  color: #495057;
}

.appointment-type {
  margin: 0;
}

.appointment-datetime {
  font-size: 0.875rem;
  color: #6c757d;
}
</style>
